/**
 * Loads the Google Maps JS API once and exposes a promise that resolves when ready.
 * Uses the Places + Geometry libraries. If REACT_APP_GOOGLE_MAPS_KEY is missing,
 * the loader rejects immediately so callers can render a fallback.
 *
 * Also installs a global ``gm_authFailure`` handler which SUPPRESSES the
 * intrusive "This page can't load Google Maps correctly" modal Google
 * shows customers when the key is misconfigured (billing off, wrong
 * referrer, API not enabled). Callers still get a rejected promise so
 * we can show a clean "type your address manually" fallback instead.
 */

let _promise = null;
let _authFailed = false;

// Install as soon as this module is imported — Google reads
// window.gm_authFailure BEFORE the API even rejects the request.
if (typeof window !== "undefined" && !window.__rd_gm_auth_handler_installed__) {
    window.__rd_gm_auth_handler_installed__ = true;
    window.gm_authFailure = () => {
        _authFailed = true;
        // eslint-disable-next-line no-console
        console.warn(
            "Google Maps auth failed — key likely restricted, disabled, " +
            "or billing off. Falling back to manual address entry.",
        );
        // Reject the loader promise so components render their fallback UI.
        if (_promise && _promise.__reject__) {
            _promise.__reject__(new Error("Google Maps auth failed"));
        }
    };
}

export function loadGoogleMaps(libraries = ["places", "geometry"]) {
    if (_authFailed) return Promise.reject(new Error("Google Maps auth failed"));
    if (_promise) return _promise;
    const key = process.env.REACT_APP_GOOGLE_MAPS_KEY;
    if (!key) {
        _promise = Promise.reject(new Error("No Google Maps API key configured"));
        return _promise;
    }
    if (typeof window !== "undefined" && window.google && window.google.maps) {
        _promise = Promise.resolve(window.google);
        return _promise;
    }
    let _reject;
    _promise = new Promise((resolve, reject) => {
        _reject = reject;
        const cb = `__gmaps_cb_${Date.now()}`;
        window[cb] = () => {
            resolve(window.google);
            delete window[cb];
        };
        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=${libraries.join(",")}&callback=${cb}&loading=async&v=quarterly`;
        script.async = true;
        script.defer = true;
        script.onerror = () => reject(new Error("Failed to load Google Maps"));
        document.head.appendChild(script);
    });
    // Expose reject so the gm_authFailure handler can trigger the fallback.
    _promise.__reject__ = _reject;
    return _promise;
}

/** Parse a Google Places `Place` object into our address shape.
 *
 *  The NEW ``PlaceAutocompleteElement`` API returns components with
 *  ``longText``/``shortText`` on the ``addressComponents`` array (camelCase).
 *  The LEGACY ``Autocomplete`` API returned ``long_name``/``short_name`` on
 *  ``address_components``. This helper handles both so callers don't have
 *  to know which API produced the object.
 */
export function parseAddressComponents(place) {
    const components =
        place.addressComponents || place.address_components || [];
    const long = (type) => {
        const c = components.find((x) => (x.types || []).includes(type));
        return c ? (c.longText ?? c.long_name ?? "") : "";
    };
    const short = (type) => {
        const c = components.find((x) => (x.types || []).includes(type));
        return c ? (c.shortText ?? c.short_name ?? "") : "";
    };
    const streetNumber = short("street_number");
    const route = long("route");

    // Location: new API uses ``.location.lat`` (number properties); legacy
    // used ``.geometry.location.lat()`` (functions).
    let lat = null, lng = null;
    if (place.location) {
        lat = typeof place.location.lat === "function"
            ? place.location.lat() : place.location.lat;
        lng = typeof place.location.lng === "function"
            ? place.location.lng() : place.location.lng;
    } else if (place.geometry?.location) {
        lat = place.geometry.location.lat();
        lng = place.geometry.location.lng();
    }

    return {
        line1: [streetNumber, route].filter(Boolean).join(" "),
        city: long("locality") || long("sublocality") || long("administrative_area_level_2"),
        state: short("administrative_area_level_1"),
        zip: short("postal_code"),
        lat,
        lng,
    };
}
